import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useApp } from "@/context/AppContext";
import { CONSOLE } from "@/constants/testIds";
import { AddressDisplay } from "@/components/AddressDisplay";
import { Hexagon, Wallet, Power, CircleDot, Activity, BarChart3, ShieldAlert, KeyRound, FileText, Settings, Cog } from "lucide-react";

const NAV = [
  { to: "/", label: "Overview", icon: BarChart3, testId: CONSOLE.navOverview, end: true },
  { to: "/operations", label: "Operations", icon: Activity, testId: CONSOLE.navOperations },
  { to: "/compliance", label: "Compliance", icon: ShieldAlert, testId: CONSOLE.navCompliance },
  { to: "/access", label: "Access Control", icon: KeyRound, testId: CONSOLE.navAccess },
  { to: "/audit", label: "Audit Log", icon: FileText, testId: CONSOLE.navAudit },
  { to: "/contract", label: "Contract", icon: Cog, testId: CONSOLE.navContract },
  { to: "/config", label: "Settings", icon: Settings, testId: CONSOLE.navConfig },
];

export default function Layout() {
  const { wallet, connect, disconnect, config, live } = useApp();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-slate-900 text-white flex items-center justify-center rounded-sm">
              <Hexagon className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="leading-tight">
              <div className="text-[13px] font-semibold tracking-tight">PUSD Issuance Console</div>
              <div className="text-[11px] text-slate-500 font-mono">{config?.network_name || "PIO Blockchain"}</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Badge variant="outline" className="font-mono text-[11px] gap-1.5 border-slate-200">
              <CircleDot className={`w-2.5 h-2.5 ${live ? "text-emerald-500" : "text-amber-500"}`} strokeWidth={3} fill="currentColor" />
              {live ? "LIVE" : "DEMO MODE"}
            </Badge>
            {wallet ? (
              <>
                <div className="hidden md:flex items-center gap-2 px-3 h-8 border border-slate-200 rounded-md bg-white">
                  <Wallet className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.75} />
                  <AddressDisplay value={wallet.address} short lead={6} tail={4} />
                  <span className="text-[10px] text-slate-400 font-mono">chain {wallet.chainId}</span>
                </div>
                <Button
                  data-testid={CONSOLE.disconnectBtn}
                  variant="outline"
                  size="sm"
                  onClick={disconnect}
                  className="rounded-md border-slate-200 text-slate-900 hover:bg-slate-50"
                >
                  <Power className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} /> Disconnect
                </Button>
              </>
            ) : (
              <Button
                data-testid={CONSOLE.connectBtn}
                onClick={connect}
                size="sm"
                className="rounded-md bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Wallet className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} /> Connect Wallet
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-12 gap-8">
        {/* Side nav */}
        <aside className="col-span-12 lg:col-span-2">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 h-9 rounded-md text-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="col-span-12 lg:col-span-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
